'use client'

import { isBefore } from 'date-fns'
import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { TKeywordMarker } from '@/entities/map'
import {
  convertHistoryToLocation,
  convertMarkerToLocation,
  type History,
  type TLocation,
  useDateRange,
  useKeyword,
  useSearchExecution,
} from '@/features/room-search'
import { type StringDateRange } from '@/shared/lib'

import { useTab } from './tab'

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const useMobileSearchBar = ({ isOpen, onClose }: Props) => {
  const [isPeriodPickerOpen, setIsPeriodPickerOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<TLocation | null>(
    null,
  )

  const { keyword, updateKeyword } = useKeyword()
  const { updateDateRange } = useDateRange()
  const { commitSearch, replayHistory } = useSearchExecution()
  const { tab, handleTabChange } = useTab()

  const updateKeywordRef = useRef(updateKeyword)

  updateKeywordRef.current = updateKeyword

  const resetSearchState = () => {
    updateKeywordRef.current('')
    setSelectedLocation(null)
    setIsPeriodPickerOpen(false)
  }

  // 오버레이가 닫힌 후에 상태를 초기화함
  // 핸들러에서 즉시 초기화하면 Radix Dialog의 close animation 구간에
  // keyword가 ''이 되어 MobileRecentRoomList가 잠깐 마운트되면서
  // 불필요한 API 요청이 발생하기 때문임
  useEffect(() => {
    if (!isOpen) {
      resetSearchState()
    }
  }, [isOpen])

  const showSearchWithoutDate = selectedLocation !== null
  const resolvedKeyword = selectedLocation?.keyword ?? keyword.trim()

  const handleKeywordSelect = (location: TKeywordMarker) => {
    setSelectedLocation(convertMarkerToLocation(location))
    setIsPeriodPickerOpen(true)
  }

  const finishSearch = () => {
    onClose()
  }

  const handleDateRangeChange = (dateRange?: StringDateRange) => {
    commitSearch({
      keyword: resolvedKeyword,
      dateRange,
      location: selectedLocation,
    })

    updateDateRange(dateRange)
    finishSearch()
  }

  const handleSearchWithoutDate = () => {
    commitSearch({
      keyword: resolvedKeyword,
      location: selectedLocation,
    })

    finishSearch()
  }

  const handleHistoryClick = (history: History) => {
    const today = new Date().setHours(0, 0, 0, 0)
    const isExpired = !history.startDate || isBefore(history.startDate, today)

    if (isExpired) {
      updateKeyword(history.name)
      updateDateRange(undefined)

      setSelectedLocation(
        history.location?.lat && history.location?.lng
          ? convertHistoryToLocation(history)
          : null,
      )

      setIsPeriodPickerOpen(true)

      return
    }

    replayHistory(history)

    onClose()
  }

  const handleKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateKeyword(event.target.value)
  }

  const handleKeywordReset = () => {
    updateKeyword('')
    setSelectedLocation(null)
  }

  const handleClose = () => {
    onClose()
  }

  return {
    keyword,
    handleKeywordChange,
    handleKeywordReset,
    isPeriodPickerOpen,
    setIsPeriodPickerOpen,
    showSearchWithoutDate,
    handleDateRangeChange,
    handleSearchWithoutDate,
    handleHistoryClick,
    handleClose,
    handleKeywordSelect,
    tab,
    handleTabChange,
  }
}
